import {
  orientedSize,
  transformPointByClass,
  composeOrientationClasses,
  canonicalCaseDimensions,
  TOPOLOGY_TRANSFORMS
} from './orientation-math.js';

export { orientedSize, transformPointByClass, composeOrientationClasses, canonicalCaseDimensions, TOPOLOGY_TRANSFORMS };

// Keep physical PCB thickness separate from the coarse component-side envelope stored in
// catalogue motherboard H.  The latter is used by packing/clearance logic, not as a solid
// board extrusion.  Air-cooler placement starts at a socket deck above the PCB surface.
export const MOTHERBOARD_PCB_THICKNESS = 1.6;
export const MOTHERBOARD_SOCKET_DECK_HEIGHT = 6.0;

export function finite(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

// Case maxPSU values describe the PSU's install length/depth, not its face width.
// Native PSU dimensions use the procedural catalogue convention:
//   SFX/SFX-L/ATX -> {l: face width, w: height, h: install depth}
//   FLEX          -> {l: install length, w: height, h: narrow face width}
// Keep this semantic conversion centralized so branch/rule validation cannot drift.
export function psuInstallLength(dims = {}, formFactor = '') {
  const ff=String(formFactor || '').trim().toUpperCase().replace(/[_ ]+/g,'-');
  if (ff.includes('FLEX')) return finite(dims?.l, finite(dims?.h,0));
  return finite(dims?.h, finite(dims?.l,0));
}

export function interiorBounds({ l, w, h }, side = 3) {
  return {
    min: [-w / 2 + side, -h / 2 + side, -l / 2 + side],
    max: [ w / 2 - side,  h / 2 - side,  l / 2 - side]
  };
}

export function boundsFromCenterSize(position, size) {
  return {
    min: [position[0]-size[0]/2, position[1]-size[1]/2, position[2]-size[2]/2],
    max: [position[0]+size[0]/2, position[1]+size[1]/2, position[2]+size[2]/2]
  };
}

export function overflowDistance(inner, box) {
  let total = 0;
  for (let axis = 0; axis < 3; axis += 1) {
    if (box.min[axis] < inner.min[axis]) total += inner.min[axis] - box.min[axis];
    if (box.max[axis] > inner.max[axis]) total += box.max[axis] - inner.max[axis];
  }
  return total;
}

export function overlapExtents(a, b) {
  return [0,1,2].map(axis => Math.max(0, Math.min(a.max[axis], b.max[axis]) - Math.max(a.min[axis], b.min[axis])));
}

export function overlapVolume(a, b) {
  const e = overlapExtents(a,b);
  return e[0] * e[1] * e[2];
}

export function boxVolume(box) {
  return Math.max(0, box.max[0]-box.min[0]) * Math.max(0, box.max[1]-box.min[1]) * Math.max(0, box.max[2]-box.min[2]);
}

export function overlapRatio(a, b) {
  const overlap = overlapVolume(a,b);
  if (!overlap) return 0;
  return overlap / Math.max(1, Math.min(boxVolume(a), boxVolume(b)));
}

export function pointInsideBounds(point, bounds, tolerance = 0) {
  return [0,1,2].every(axis => point[axis] >= bounds.min[axis]-tolerance && point[axis] <= bounds.max[axis]+tolerance);
}

export function canonicalPlacementToWorld(candidate, transformClass) {
  return {
    ...candidate,
    position: transformPointByClass(candidate.position, transformClass),
    orientationClass: composeOrientationClasses(transformClass, candidate.orientationClass)
  };
}

export function canonicalEnvelopeToWorld(envelope, transformClass) {
  const worldPosition = transformPointByClass(envelope.position, transformClass);
  // Envelope is axis-aligned in canonical space; treat its canonical axes as a box with class 0,
  // then rotate the size by the case transform.
  const worldSize = orientedSize(envelope.size, transformClass);
  return { position: worldPosition, size: worldSize };
}

export function componentLocalSize(type, dims = {}) {
  const l = finite(dims.l, 0), w = finite(dims.w, 0), h = finite(dims.h, 0);
  if (type === 'motherboard') return [w || 170, MOTHERBOARD_PCB_THICKNESS, l || 170];
  // Structural keepout for VRM/RAM/connectors above the PCB. The rendered motherboard
  // remains the 1.6 mm PCB; this one-sided envelope is used only by the packing solver.
  if (type === 'motherboard_keepout') return [w || 170, h || 44, l || 170];
  if (type === 'gpu') return [l || 300, h || 137, w || 50];
  if (type === 'psu') return [l || 125, w || 63.5, h || 100];
  if (type === 'aircooler' || type === 'cooler') return [w || 95, h || 47, l || 95];
  if (type === 'radiator' || type === 'aio') return [w || 120, h || 27, l || 274];
  if (type === 'fan' || type === 'fan_reservation') return [l || 120, h || 25, w || 120];
  return [w || 50, h || 50, l || 100];
}

export function socketLocalForMotherboard(dims = {}, formFactor = 'ITX') {
  const l = finite(dims.l, 170), w = finite(dims.w, 170);
  const ff = String(formFactor || 'ITX').toUpperCase();
  // This is deliberately form-factor level, not model-specific PCB truth. It keeps the
  // CPU socket in the upper/rear region as boards grow instead of reusing an ITX point.
  const xFactor = ff === 'ATX' || ff === 'MATX' ? 0.08 : (ff === 'DTX' ? 0.04 : 0.02);
  const zFactor = ff === 'ATX' ? -0.18 : (ff === 'MATX' ? -0.16 : (ff === 'DTX' ? -0.08 : -0.02));
  return [w * xFactor, (MOTHERBOARD_PCB_THICKNESS / 2) + MOTHERBOARD_SOCKET_DECK_HEIGHT, l * zFactor];
}

export function transformLocalPoint(localPoint, position, orientationClass) {
  const rotated = transformPointByClass(localPoint, orientationClass);
  return [rotated[0]+position[0], rotated[1]+position[1], rotated[2]+position[2]];
}
